import { useEffect, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useMutation } from '@apollo/client';
import { toast } from 'react-toastify';
import NumericInput from '../../NumericInput';
import { GET_CAR, UPDATE_CAR } from '../../../services/carService/queries';
import { getBrand } from '../../../constants/brands';

const Title = styled.h2`
  ${tw`text-lg font-bold mb-1 text-text-primary`}
`;

const Description = styled.p`
  ${tw`text-sm text-text-secondary mb-4 leading-relaxed`}
`;

const Warning = styled.p`
  ${tw`text-sm text-amber-800 bg-amber-50 border border-amber-100 rounded-lg p-3 mb-4 leading-relaxed`}
`;

const FieldGroup = styled.div`
  ${tw`mb-4`}
`;

const FieldLabel = styled.label`
  ${tw`block text-sm font-medium text-text-secondary mb-2`}
`;

const Required = styled.span`
  ${tw`text-red-500 ml-0.5`}
`;

const InputGroup = styled.div`
  ${tw`flex items-center gap-2`}
`;

const CurrencySymbol = styled.div`
  ${tw`flex items-center justify-center bg-bg-secondary border border-border-default border-r-0 rounded-l-lg h-12 w-12 text-text-secondary font-medium shrink-0`}
`;

const Input = styled(NumericInput)`
  ${tw`flex-1 h-12 px-4 border border-border-default rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-black transition-all text-text-primary`}
`;

const ApplyButton = styled.button<{ disabled?: boolean }>`
  ${tw`w-full py-4 bg-black text-white rounded-card font-bold text-lg transition-all shadow-card-hover active:scale-[0.98]`}
  ${({ disabled }) => disabled && tw`opacity-50 cursor-not-allowed`}
  &:hover:not(:disabled) {
    ${tw`bg-black`}
  }
`;

const ValidationHint = styled.p`
  ${tw`text-sm text-red-600 mb-4`}
`;

export interface DynamicPricingPanelProps {
  carId: string;
  weekdayPrice?: number | null;
  weekendPrice?: number | null;
  holidayPrice?: number | null;
  /**
   * Spec 014a: the brand uses one daily rate. Only that rate is edited, and
   * it is saved as the weekend and holiday price too.
   */
  weekdayOnly?: boolean;
}

const DynamicPricingPanel: React.FC<DynamicPricingPanelProps> = ({
  carId,
  weekdayPrice: initialWeekday,
  weekendPrice: initialWeekend,
  holidayPrice: initialHoliday,
  weekdayOnly = false,
}) => {
  const [weekdayPrice, setWeekdayPrice] = useState<number | null>(initialWeekday ?? null);
  const [weekendPrice, setWeekendPrice] = useState<number | null>(initialWeekend ?? null);
  const [holidayPrice, setHolidayPrice] = useState<number | null>(initialHoliday ?? null);
  const [showValidation, setShowValidation] = useState(false);

  useEffect(() => {
    setWeekdayPrice(initialWeekday ?? null);
    setWeekendPrice(initialWeekend ?? null);
    setHolidayPrice(initialHoliday ?? null);
  }, [initialWeekday, initialWeekend, initialHoliday]);

  const [updateCar, { loading }] = useMutation(UPDATE_CAR, {
    refetchQueries: [{ query: GET_CAR, variables: { id: carId } }],
    onCompleted: () => {
      toast.success('Dynamic pricing applied to all future days');
    },
    onError: error => {
      toast.error(error.message || 'Failed to apply dynamic pricing');
    },
  });

  const isDynamicValid = weekdayOnly
    ? weekdayPrice != null && weekdayPrice > 0
    : weekdayPrice != null &&
      weekdayPrice > 0 &&
      weekendPrice != null &&
      weekendPrice > 0 &&
      holidayPrice != null &&
      holidayPrice > 0;

  const handleApply = async () => {
    if (!isDynamicValid) {
      setShowValidation(true);
      return;
    }

    setShowValidation(false);
    await updateCar({
      variables: {
        id: carId,
        // Weekday-only (one uniform rate): the server accepts weekend and
        // holiday prices equal to the daily rate.
        updateCarInput: weekdayOnly
          ? { weekdayPrice, weekendPrice: weekdayPrice, holidayPrice: weekdayPrice }
          : {
              weekdayPrice,
              weekendPrice,
              holidayPrice,
            },
      },
    });
  };

  return (
    <div tw="pb-20">
      <Title>{weekdayOnly ? 'Daily rate' : 'Dynamic pricing'}</Title>
      <Description>
        {weekdayOnly
          ? `Set your daily rate. ${getBrand().name} uses one rate for every day of the week.`
          : 'Set weekday, weekend, and holiday daily rates for every day from today through the next year. US public holidays use the holiday rate.'}
      </Description>
      <Warning>
        {weekdayOnly
          ? 'Saving sets this rate across the whole future calendar.'
          : 'Applying dynamic pricing replaces custom daily prices across the whole future calendar. Use Prices → Adjust or Set to change individual dates afterward.'}
      </Warning>

      <FieldGroup>
        <FieldLabel>
          {weekdayOnly ? 'Daily price ($)' : 'Weekday price ($)'}
          <Required>*</Required>
        </FieldLabel>
        <InputGroup>
          <CurrencySymbol>$</CurrencySymbol>
          <Input
            placeholder="Enter weekday price"
            value={weekdayPrice}
            onValueChange={val => {
              setWeekdayPrice(val);
              setShowValidation(false);
            }}
            minValue={0}
          />
        </InputGroup>
      </FieldGroup>

      {!weekdayOnly && (
        <FieldGroup>
          <FieldLabel>
            Weekend price ($)
            <Required>*</Required>
          </FieldLabel>
          <InputGroup>
            <CurrencySymbol>$</CurrencySymbol>
            <Input
              placeholder="Enter weekend price"
              value={weekendPrice}
              onValueChange={val => {
                setWeekendPrice(val);
                setShowValidation(false);
              }}
              minValue={0}
            />
          </InputGroup>
        </FieldGroup>
      )}

      {!weekdayOnly && (
        <FieldGroup>
          <FieldLabel>
            Holiday price ($)
            <Required>*</Required>
          </FieldLabel>
          <InputGroup>
            <CurrencySymbol>$</CurrencySymbol>
            <Input
              placeholder="Enter holiday price"
              value={holidayPrice}
              onValueChange={val => {
                setHolidayPrice(val);
                setShowValidation(false);
              }}
              minValue={0}
            />
          </InputGroup>
        </FieldGroup>
      )}

      {showValidation && !isDynamicValid && (
        <ValidationHint>
          {weekdayOnly
            ? 'The daily price is required and must be greater than zero.'
            : 'All three prices are required and must be greater than zero.'}
        </ValidationHint>
      )}

      <ApplyButton type="button" disabled={loading || !isDynamicValid} onClick={handleApply}>
        {loading ? 'Applying...' : weekdayOnly ? 'Save daily rate' : 'Apply dynamic pricing'}
      </ApplyButton>
    </div>
  );
};

export default DynamicPricingPanel;
